// ./src/styles/theme/index.ts
// ============================================
// CarExpenses Main Theme Configuration
// Automotive meets finance - trust, precision, motion
// ============================================

import { createTheme, MantineThemeOverride } from "@mantine/core";
import {
  brand,
  accent,
  road,
  fuel,
  success,
  error,
  warning,
  neutral,
  gradients,
  backgrounds,
  textColors,
  borderColors,
  expenseColors,
  chartColors,
} from "./colors";
import { fonts } from "./fonts";

export const carExpensesTheme: MantineThemeOverride = createTheme({
  // ============================================
  // Color Configuration
  // ============================================
  colors: {
    brand,
    accent,
    road,
    fuel,
    success,
    error,
    warning,
    neutral,
  },

  // Primary color reference - Forest Green
  primaryColor: "brand",
  primaryShade: { light: 6, dark: 5 },

  // ============================================
  // Typography
  // ============================================
  fontFamily: fonts.primary,
  fontFamilyMonospace: fonts.monospace,

  headings: {
    fontFamily: fonts.display,
    fontWeight: "700",
    sizes: {
      h1: {
        fontSize: fonts.sizes.h1,
        lineHeight: "1.15",
        fontWeight: "800",
      },
      h2: {
        fontSize: fonts.sizes.h2,
        lineHeight: "1.2",
        fontWeight: "700",
      },
      h3: {
        fontSize: fonts.sizes.h3,
        lineHeight: "1.25",
        fontWeight: "700",
      },
      h4: {
        fontSize: fonts.sizes.h4,
        lineHeight: "1.35",
        fontWeight: "600",
      },
      h5: {
        fontSize: fonts.sizes.h5,
        lineHeight: "1.4",
        fontWeight: "600",
      },
      h6: {
        fontSize: fonts.sizes.h6,
        lineHeight: "1.5",
        fontWeight: "600",
      },
    },
  },

  // ============================================
  // Spacing
  // ============================================
  spacing: {
    xs: "0.25rem", // 4px
    sm: "0.5rem", // 8px
    md: "1rem", // 16px
    lg: "1.5rem", // 24px
    xl: "2rem", // 32px
  },

  // ============================================
  // Border Radius
  // Slightly more rounded for friendly automotive feel
  // ============================================
  radius: {
    xs: "0.25rem", // 4px - Badges, small elements
    sm: "0.375rem", // 6px - Inputs, buttons
    md: "0.625rem", // 10px - Cards
    lg: "1rem", // 16px - Large cards
    xl: "1.5rem", // 24px - Hero elements
  },

  // ============================================
  // Shadows
  // Subtle, professional shadows
  // ============================================
  shadows: {
    xs: "0 1px 2px 0 rgb(0 0 0 / 0.04)",
    sm: "0 1px 3px 0 rgb(0 0 0 / 0.08), 0 1px 2px -1px rgb(0 0 0 / 0.08)",
    md: "0 4px 6px -1px rgb(0 0 0 / 0.08), 0 2px 4px -2px rgb(0 0 0 / 0.08)",
    lg: "0 10px 15px -3px rgb(0 0 0 / 0.08), 0 4px 6px -4px rgb(0 0 0 / 0.08)",
    xl: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
  },

  // ============================================
  // Default Settings
  // ============================================
  defaultRadius: "md",
  cursorType: "pointer",
  respectReducedMotion: true,
  focusRing: "auto",
  scale: 1,

  // ============================================
  // Custom Properties
  // ============================================
  other: {
    // === Brand colors (quick access) ===
    brandPrimary: "#16a34a", // Main forest green
    brandDark: "#14532d", // Dark forest
    brandLight: "#f0fdf4", // Light mint
    accentAmber: "#f59e0b", // Amber highlight

    // === Semantic colors ===
    successGreen: "#10b981",
    errorRed: "#ef4444",
    warningOrange: "#f97316",
    infoCyan: "#06b6d4",

    // === Background variations ===
    backgrounds,

    // === Text colors ===
    text: textColors,

    // === Border colors ===
    borders: borderColors,

    // === Gradients ===
    gradients,

    // === Expense category colors ===
    expenses: expenseColors,

    // === Chart colors ===
    charts: chartColors,

    // === Brand shadows ===
    shadowBrand: "0 4px 14px 0 rgb(22 101 52 / 0.2)",
    shadowBrandLg: "0 10px 40px 0 rgb(22 101 52 / 0.25)",
    shadowAccent: "0 4px 14px 0 rgb(245 158 11 / 0.25)",

    // === Glow effects ===
    glowBrand: "0 0 20px rgb(22 163 74 / 0.3)",
    glowAccent: "0 0 20px rgb(251 191 36 / 0.4)",

    // === Animation durations ===
    animations: {
      instant: "50ms",
      fast: "150ms",
      normal: "250ms",
      slow: "350ms",
      slower: "500ms",
    },

    // === Easing functions ===
    easing: {
      default: "cubic-bezier(0.4, 0, 0.2, 1)",
      in: "cubic-bezier(0.4, 0, 1, 1)",
      out: "cubic-bezier(0, 0, 0.2, 1)",
      inOut: "cubic-bezier(0.4, 0, 0.2, 1)",
      bounce: "cubic-bezier(0.68, -0.55, 0.265, 1.55)",
    },

    // === Z-index scale ===
    zIndex: {
      hide: -1,
      base: 0,
      dropdown: 1000,
      sticky: 1020,
      fixed: 1030,
      modalBackdrop: 1040,
      modal: 1050,
      popover: 1060,
      tooltip: 1070,
      notification: 1080,
    },

    // === Container max-widths ===
    container: {
      xs: "540px",
      sm: "720px",
      md: "960px",
      lg: "1140px",
      xl: "1320px",
      full: "100%",
    },

    // === Layout dimensions ===
    layout: {
      sidebarCollapsed: "64px",
      sidebarExpanded: "280px",
      headerHeight: "64px",
      mobileHeaderHeight: "56px",
      footerHeight: "48px",
    },
  },
});

// ============================================
// Re-exports for convenience
// ============================================
export { fonts, textStyles, googleFontsUrl, fontFaceCSS } from "./fonts";
export {
  brand,
  accent,
  road,
  fuel,
  success,
  error,
  warning,
  neutral,
  semanticColors,
  gradients,
  backgrounds,
  textColors,
  borderColors,
  expenseColors,
  chartColors,
  colorPalette,
} from "./colors";

export default carExpensesTheme;
